import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Contract, PaginatedResponse } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';


const statusVariant = (status: string) => {
    switch (status) {
        case 'active': return 'success' as const;
        case 'closed': return 'secondary' as const;
        case 'abandoned': return 'destructive' as const;
        default: return 'default' as const;
    }
};

export default function ContractsPage() {
    const { user } = useAuth();
    const { data, isLoading } = useQuery({
        queryKey: ['contracts'],
        queryFn: () => api.get<PaginatedResponse<Contract>>('/contracts'),
    });

    const contracts = data?.data ?? [];

    return (
        <div className="space-y-6">
            <h1 className="text-xl font-semibold">Your Contracts</h1>

            {isLoading ? (
                <p className="text-sm text-slate-400">Loading...</p>
            ) : contracts.length === 0 ? (
                <Card>
                    <CardContent className="py-8 text-center text-sm text-slate-400">
                        No contracts yet.{' '}
                        <Link to="/marketplace" className="text-violet-400 hover:underline">
                            Browse templates
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {contracts.map((contract) => (
                        <Link key={contract.id} to={`/contracts/${contract.id}`} className="block">
                            <Card className="transition-shadow hover:shadow-lg hover:shadow-slate-900/50">
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="min-w-0">
                                            <p className="truncate font-medium">{contract.title}</p>
                                            <p className="text-xs text-slate-400">
                                                {contract.dom?.id === user?.id
                                                    ? <>Sub: <span className="text-violet-400">{contract.sub!.display_name}</span></>
                                                    : <>Dom: <span className="text-violet-400">{contract.dom!.display_name}</span></>}
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500">
                                                Started {formatDate(contract.start_date)}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <Badge variant={statusVariant(contract.status)}>
                                                {contract.status}
                                            </Badge>
                                            <span className="text-lg font-bold">
                                                {formatCurrency(contract.current_balance)}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
